import { snakeCase } from 'lodash';
import { Model, IQueryFilterWrapper, QueryFilter } from '@hub-components';
import { WidgetConfig } from '../interfaces/widget-config.interface';

export type ItemType = 'advertiser' | 'agency';

export const ITEM_TYPES: ItemType[] = ['advertiser', 'agency'];

export interface DataSourceConfig {
  id?: number;
  name: string;
  display_name?: string;
  index: number;
  advertiser_dimension?: string;
  agency_dimension?: string;
  border_color?: string;
  filter?: IQueryFilterWrapper[];
  overview?: {
    hidden?: boolean,
    pages: {
      id?: number;
      index?: number;
      widgets: WidgetConfig[];
    }[];
  };
  breakdown?: Breakdown;
  breakdown_metrics?: {
    default?: string[];
    [key: string]: string[];
  };
}

export function dimensionForItemType(datasource: DataSourceConfig, itemType: ItemType | undefined): string | undefined {
  return itemType === 'agency' ? datasource.agency_dimension : datasource.advertiser_dimension;
}

export type BreakdownOptions = {
  color?: string;
  preview_type?: 'call' | 'image';
  primary_attribute?: string;
  secondary_attribute?: string;
  date_attribute?: string;
  state_attribute?: string;
  city_attribute?: string;
  recording_player_attribute?: string;
  phone_number_attribute?: string;
};

export type Breakdown = {
  id?: number;
  dimension: string;
  options?: BreakdownOptions;
  auth?: string;
  breakdowns?: Breakdown[];
};

export interface ConfigJson {
  search_dataset?: string;
  search_dimension?: string;
  search_agency_dimension?: string;
  type?: string;
  datasources?: DataSourceConfig[];
}

export class Config extends Model {
  searchDataset: string;
  searchDimension: string;
  searchAgencyDimension: string;
  type?: string;
  datasources: DataSourceConfig[];

  constructor(attributes: any = {}) {
    super(attributes);
    if (Array.isArray(attributes.datasources)) {
      attributes.datasources.forEach((datasource: any) => {
        datasource.filter = datasource.filter?.map(filter => ({ expressions: filter.expressions.map(expression => new QueryFilter(expression)) }));
        this.migrateLegacyDimensionFields(datasource);
      });
    }
  }

  private migrateLegacyDimensionFields(datasource: any): void {
    if (datasource.dimension && !datasource.advertiser_dimension) {
      const legacyType = this.inferLegacyType(datasource);
      if (legacyType === 'agency' && !datasource.agency_dimension) {
        datasource.agency_dimension = datasource.dimension;
      } else {
        datasource.advertiser_dimension = datasource.dimension;
      }
    }
    delete datasource.dimension;
  }

  private inferLegacyType(datasource: any): ItemType {
    if (this.searchAgencyDimension && datasource.dimension === this.searchAgencyDimension && datasource.dimension !== this.searchDimension) {
      return 'agency';
    }
    return 'advertiser';
  }

  public asString(): string {
    return JSON.stringify(this.asJsonObject(), null, 2);
  }

  public asJsonObject(): ConfigJson {
    const config = {};
    for (let key in this) {
      let value = this[key];
      if (value !== null && key !== '_destroy') {
        let attribute = snakeCase(key);
        config[attribute] = value;
      }
    }
    config['datasources'] = this.datasources.map(datasource => {
      return { ...datasource, filter: datasource.filter?.map(filter => {
        return { ...filter, expressions: filter.expressions.map(expression => expression.asJson()) };
      }) };
    });
    return structuredClone(config);
  }

  public validate(): boolean {
    const config: any = this;
    const isString = (val: any, part: string): val is string => {
      if (typeof val === 'string') {
        return true;
      }
      throw new TypeError(`${part}: is not a string`);
    };
    const isNonEmptyString = (val: any, part: string): val is string => {
      if (typeof val === 'string' && val.length > 0) {
        return true;
      }
      throw new TypeError(`${part}: is required`);
    };
    const isNumber = (val: any, part: string): val is number => {
      if (typeof val === 'number') {
        return true;
      }
      throw new TypeError(`${part}: is not a number`);
    };
    const isObject = <T extends object>(val: any, part: string): val is T => {
      if (typeof val === 'object') {
        return true;
      }
      throw new TypeError(`${part}: is not an object`);
    };
    const isArray = (val: any, type: string, part: string): val is any[] => {
      if (Array.isArray(val) && val.every((v: any) => typeof v === type)) {
        return true;
      }
      throw new TypeError(`${part}: is not an array of type ${type}`);
    };

    const isValidPreviewType = (val: any): val is ('call' | 'image') => {
      if (val === 'call' || val === 'image') {
        return true;
      }
      throw new TypeError('Preview type: must be "call" or "image"');
    };

    const areValidBreakdownOptions = (options: any) => {
      if (options.color) {
        isString(options.color, 'options.color');
      }
      if (options.preview_type) {
        isValidPreviewType(options.preview_type);
        isString(options.preview_type, 'options.preview_type');
        if (options.preview_type === 'image') {
          if (!options.primary_attribute) {
            throw new TypeError('options.primary_attribute is required for image preview type');
          }
          isString(options.primary_attribute, 'options.primary_attribute');
        }
      }
      if (options.secondary_attribute) {
        isString(options.secondary_attribute, 'options.secondary_attribute');
      }
      if (options.date_attribute) {
        isString(options.date_attribute, 'options.date_attribute');
      }
      if (options.state_attribute) {
        isString(options.state_attribute, 'options.state_attribute');
      }
      if (options.city_attribute) {
        isString(options.city_attribute, 'options.city_attribute');
      }
      if (options.recording_player_attribute) {
        isString(options.recording_player_attribute, 'options.recording_player_attribute');
      }
      if (options.phone_number_attribute) {
        isString(options.phone_number_attribute, 'options.phone_number_attribute');
      }
    };

    if (config.datasources === undefined) {
      return true;
    }

    isString(config.searchDataset, 'search_dataset');
    if (config.datasources.length > 0 && !config.searchDimension && !config.searchAgencyDimension) {
      throw new TypeError('At least one of search_dimension or search_agency_dimension is required when datasources are configured');
    }
    if (config.searchAgencyDimension) {
      isString(config.searchAgencyDimension, 'search_agency_dimension');
    }
    if (config.searchDimension) {
      isString(config.searchDimension, 'search_dimension');
    }
    isObject(config.datasources, 'datasources');
    isArray(config.datasources, 'object', 'datasources');
    config.datasources.forEach((datasource, i) => {
      isNumber(datasource.index, `datasources[${i}].index`);
      isNonEmptyString(datasource.name, `datasources[${i}].name`);
      if (!datasource.advertiser_dimension && !datasource.agency_dimension) {
        throw new TypeError(`datasources[${i}]: at least one of advertiser_dimension or agency_dimension is required`);
      }
      if (datasource.advertiser_dimension) {
        isString(datasource.advertiser_dimension, `datasources[${i}].advertiser_dimension`);
      }
      if (datasource.agency_dimension) {
        isString(datasource.agency_dimension, `datasources[${i}].agency_dimension`);
      }

      if (datasource.border_color) {
        isString(datasource.border_color, `datasources[${i}].border_color`);
      }

      if (datasource.overview) {
        isObject(datasource.overview, `datasources[${i}].overview`);
        isArray(datasource.overview.pages, 'object', `datasources[${i}].overview.pages`);

        datasource.overview.pages.forEach((page, j) => {
          isObject(page, `datasources[${i}].overview.pages[${j}]`);
          isNumber(page.index, `datasources[${i}].overview.pages[${j}].index`);
          isArray(page.widgets, 'object', `datasources[${i}].overview.pages[${j}].widgets`);
          page.widgets.forEach((widget, k) => {
            if (widget.options) {
              isObject(widget.options, `datasources[${i}].overview.pages[${j}].widgets[${k}].options`);
            }
            isString(widget.type, `datasources[${i}].overview.pages[${j}].widgets[${k}].type`);
            isNumber(widget.index, `datasources[${i}].overview.pages[${j}].widgets[${k}].index`);
            isNumber(widget.size, `datasources[${i}].overview.pages[${j}].widgets[${k}].size`);
            isObject(widget.inputs, `datasources[${i}].overview.pages[${j}].widgets[${k}].inputs`);
            isString(widget.rollup, `datasources[${i}].overview.pages[${j}].widgets[${k}].rollup`);
            isArray(
              widget.inputs.metrics,
              'string',
              `datasources[${i}].overview.pages[${j}].widgets[${k}].inputs.metrics`,
            );
          });
        });
      }

      if (datasource.breakdowns) {
        throw new TypeError('The first breakdown level should be called "breakdown" (singular)');
      }

      if (datasource.breakdown && Object.keys(datasource.breakdown).length > 0) {
        isObject(datasource.breakdown, `datasources[${i}].breakdown`);
        isString(datasource.breakdown.dimension, `datasources[${i}].breakdown.dimension`);

        if (datasource.breakdown.breakdowns) {
          isArray(datasource.breakdown.breakdowns, 'object', `datasources[${i}].breakdown.breakdowns`);
          datasource.breakdown.breakdowns.forEach((breakdown, k) => {
            isString(breakdown.dimension, `datasources[${i}].breakdown.breakdowns[${k}].dimension`);
            if (breakdown.options) {
              areValidBreakdownOptions(breakdown.options);
            }
          });
        }

        if (datasource.breakdown.options) {
          isObject(datasource.breakdown.options, `datasources[${i}].breakdown.options`);
          areValidBreakdownOptions(datasource.breakdown.options);
        }
      }

      if (datasource.breakdown && Object.keys(datasource.breakdown).length > 0) {
        const hasAnyMetric = datasource.breakdown_metrics &&
          Object.values(datasource.breakdown_metrics).some((arr: string[]) => arr?.length > 0);
        if (!hasAnyMetric) {
          throw new TypeError(`datasources[${i}].breakdown_metrics: at least one metric is required when breakdown is configured`);
        }
      }

      if (datasource.breakdown && datasource.breakdown_metrics) {
        isObject(datasource.breakdown_metrics, `datasources[${i}].breakdown_metrics`);
        if (datasource.breakdown_metrics.default) {
          isArray(datasource.breakdown_metrics.default, 'string', `datasources[${i}].breakdown_metrics.default`);
        }
      }
    });
    return true;
  }
}
